import { makeStyles, tokens } from '@fluentui/react-components';
import { CallInbound16Regular, CallOutbound16Regular } from '@fluentui/react-icons';
import type { MultiTableViewDefinition, TableRecord } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import { Calendar, type CalendarAppointment } from '@powerportalspro/react-calendar';
import { useMemo } from 'react';
import { ACCOUNT_ID } from '../demos/DetailPageRecords';

// Schmeler and Sons' appointments and phone calls. A phone call is drawn with which way it went,
// and every tooltip says what matters about its row. The view names the columns they show, so the
// calendar reads them.

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Calendars.CalendarTemplatesDemo';

const account = { tableName: 'account', id: ACCOUNT_ID };

const useStyles = makeStyles({
  // A phone call on the calendar: one line, which way it went before its subject.
  call: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
    minWidth: 0,
    maxWidth: '100%',
    '& svg': {
      flexShrink: 0,
      width: '14px',
      height: '14px',
    },
  },
  subject: {
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});

// The raw value behind a column.
function read(record: TableRecord | null | undefined, column: string): unknown {
  const cell = record?.properties?.[column];
  return cell && typeof cell === 'object' && 'value' in cell ? cell.value : undefined;
}

export function CalendarTemplatesSample() {
  const styles = useStyles();
  const t = useT();

  const views = useMemo<MultiTableViewDefinition[]>(
    () => [
      {
        id: '4b2d8f61-9c3e-4a7d-b5e0-1f6a2c8d9e31',
        displayName: t(`${STRINGS_BASE}.view-name`),
        tables: [
          { table: 'appointment' },
          {
            table: 'phonecall',
            fetchXml: '<fetch><entity name="phonecall"><attribute name="directioncode" /><attribute name="phonenumber" /></entity></fetch>',
          },
        ],
      },
    ],
    [t],
  );

  const callSummary = (record: TableRecord) => {
    const number = read(record, 'phonenumber');
    return t(`${STRINGS_BASE}.${read(record, 'directioncode') === true ? 'call-to' : 'call-from'}`, [
      typeof number === 'string' ? number : '',
    ]);
  };

  const tooltip = (appointment: CalendarAppointment) => (
    <>
      <strong>{appointment.subject}</strong>
      {appointment.record && (
        <div>
          {appointment.table === 'phonecall'
            ? callSummary(appointment.record)
            : appointment.record.formattedValues?.['location']}
        </div>
      )}
    </>
  );

  return (
    <Calendar
      regarding={account}
      customViewDefinitions={views}
      defaultLayout="month"
      height="640px"
      appointmentTooltip={tooltip}
      templates={{
        phonecall: (item) => (
          <span className={styles.call}>
            {read(item.record, 'directioncode') === true ? <CallOutbound16Regular /> : <CallInbound16Regular />}
            <span className={styles.subject}>{item.title}</span>
          </span>
        ),
      }}
    />
  );
}
